import React from 'react';
import { ArrowUpRight } from 'lucide-react';
import { TONE_BORDER, TONE_SOFT } from './tone';

/* The Company page pairs: indigo/teal chips on white, the figure in the same
 * hue. Neutral takes the brand teal so an untoned tile still has colour. */
const CHIP = {
  neutral: 'bg-brand-100 text-brand-700',
  success: 'bg-success-bg text-success-fg',
  warning: 'bg-warning-bg text-warning-fg',
  danger: 'bg-danger-bg text-danger-fg',
  info: 'bg-info-bg text-info-fg',
  brand: 'bg-brand-100 text-brand-700'
};

const FIGURE = {
  neutral: 'text-gray-900',
  success: 'text-success-fg',
  warning: 'text-warning-fg',
  danger: 'text-danger-fg',
  info: 'text-info-fg',
  brand: 'text-brand-700'
};

const CARD_BORDER = {
  neutral: 'border-hairline',
  success: 'border-success-border',
  warning: 'border-warning-border',
  danger: 'border-danger-border',
  info: 'border-info-border',
  brand: 'border-brand-100'
};

/**
 * A single figure with its name above it — the row of summary numbers at the
 * top of a report screen.
 *
 * The shape is the one Sales already uses (`builder/components/ui/
 * MetricCard.jsx`, the tiles on the Leads list): a tinted icon chip on the
 * left, a small uppercase label, a large bold figure, and — when the tile
 * filters something — an arrow in the corner saying so. The newer screens had
 * been drawing a plainer box, which is why a Labour report and the Leads list
 * did not look like the same product even after the colours matched.
 *
 * Before that, seven screens each shipped their own copy, disagreeing on label
 * size, value size, the gap between them, the resting background, and even the
 * name of the emphasis prop (`tone`, `accent`, `strong`).
 *
 * `variant="card"` draws it exactly like the Company page's stat cards — the
 * reference HR/Labour were asked to follow on 28 Sep 2026: always a white
 * card, a 44px tinted icon square, and the figure in the tone's colour (tone
 * colours the chip, figure and hairline instead of tinting the whole card).
 * It is opt-in so screens outside HR/Labour (Time on System) keep the plain
 * tile until they are asked to change.
 *
 *   <Tile label="Outstanding" value="₹7,12,600" hint="Still owed"
 *         icon={<Wallet size={18} />} tone="warning" />
 *
 *   <Tile label="Cold" value={47} icon={<Snowflake size={18} />}
 *         onClick={() => filterBy('cold')} />   // gains the ↗ affordance
 */
const Tile = ({
  label,
  value,
  hint,
  icon,
  tone = 'neutral',
  variant = 'plain',
  onClick,
  className = ''
}) => {
  const interactive = typeof onClick === 'function';
  const isCard = variant === 'card';

  return (
    <div
      role={interactive ? 'button' : undefined}
      tabIndex={interactive ? 0 : undefined}
      onClick={onClick}
      onKeyDown={
        interactive
          ? (e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                onClick();
              }
            }
          : undefined
      }
      className={
        isCard
          ? `relative flex items-center gap-4 rounded-xl border bg-white px-4 py-3 shadow-sm sm:px-5 sm:py-4 ${
              CARD_BORDER[tone] ?? CARD_BORDER.neutral
            } ${interactive ? 'cursor-pointer pr-10 transition-shadow hover:shadow-md' : ''} ${className}`
          : `relative flex items-center gap-3 rounded-panel border p-3 ${
              TONE_BORDER[tone] ?? TONE_BORDER.neutral
            } ${interactive ? 'cursor-pointer transition-shadow hover:shadow-card' : ''} ${
              interactive ? 'pr-8' : ''
            } ${className}`
      }>
      {/* Hidden below sm: at phone width the chip costs more room than the
          figure it decorates, which is the trade MetricCard already makes.
          The card chip carries a 20px icon; callers pass 16-18px ones. */}
      {icon && (
        <span
          className={
            isCard
              ? `hidden h-11 w-11 shrink-0 items-center justify-center rounded-lg sm:flex [&>svg]:h-5 [&>svg]:w-5 ${
                  CHIP[tone] ?? CHIP.neutral
                }`
              : `hidden h-9 w-9 shrink-0 items-center justify-center rounded-control sm:flex ${
                  TONE_SOFT[tone] ?? TONE_SOFT.neutral
                }`
          }>
          {icon}
        </span>
      )}

      <div className="min-w-0 flex-1">
        <div className="text-xs font-medium uppercase leading-tight tracking-wide text-gray-500">
          {label}
        </div>
        <div
          className={
            isCard
              ? `mt-1 text-2xl font-bold leading-none tabular-nums sm:text-[1.75rem] ${
                  FIGURE[tone] ?? FIGURE.neutral
                }`
              : 'mt-0.5 text-xl font-bold leading-none tabular-nums text-gray-900 sm:text-2xl'
          }>
          {value}
        </div>
        {hint && <div className="mt-1 truncate text-xs text-gray-500">{hint}</div>}
      </div>

      {interactive && (
        <span
          aria-hidden="true"
          className="absolute bottom-2 right-2 inline-flex h-5 w-5 items-center justify-center rounded-pill bg-brand-100 text-brand-700">
          <ArrowUpRight size={14} />
        </span>
      )}
    </div>
  );
};

export default Tile;
